import classNames from "classnames"
import { cloneDeep } from "lodash-es"
import { observer } from "mobx-react"
import React, { useCallback, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useLocation, useNavigate } from "react-router-dom"

import { APICode } from "@/api/APICode"
import ShopifyGraphql from "@/api/shopify-graphql/shopify-graphql"
import TPAPI from "@/api/tracking-page/index"
import { Container, TabPage } from "@/component"
import SaveModule from "@/component/SaveModule"
import { useEditorShortcuts } from "@/hooks/useEditorShortcuts"
import {
  FeaturesText,
  ITrackingPageOptions,
  ProductRecommendIntent,
} from "@/models/ITrackingPageOptions"
import AddTrackingPageModal from "@/pages/Dashboard/components/AddTrackingPageModal"
import { initModulesStore } from "@/pages/TrackingPage/EstimatedDeliveryTime/listener"
import { UpdateURLMessage, UpdateURLModal } from "@/pages/TrackingPage/Modals"
import {
  fetchTrackingPageBaseData,
  fetchTrackingPageProductCollects, filterEmptyFeatureTextTrans,
} from "@/pages/TrackingPage/trackingPageDataFetch"
import { checkAddTrackingPage, getShortTrackLink } from "@/pages/TrackingPage/utils"
import stores from "@/stores"
import { deepDiffBetweenObjects } from "@/utils/ObjectUtils"
import toast from "@/utils/toast"
import TrackingPageUtils, { trackingPageHistory } from "@/utils/TrackingPageUtils"

/**
 * 获得差异配置信息
 */
export function getDiffOptions(): Partial<ITrackingPageOptions> {
  return deepDiffBetweenObjects(stores.trackingPageStore.options, TrackingPageUtils.getInitOptions())
}

/**
 * 跟踪页面主组件
 */
const TrackingPage: React.FC = observer(() => {
  const { t } = useTranslation(["tracking_page", "common"])
  const location = useLocation()
  const navigate = useNavigate()

  const { trackingPageStore, commonStore, eddModulesStore, userInfoSore } = stores
  const loading = trackingPageStore.loadedDataTime === 0

  const isThemeLayoutCustomizeFullWidthRoute = location.pathname === "/tracking-page/customizeBackdrop"
    || location.pathname === "/tracking-page/customizeShowcase"


  // Tracking URL Status
  const [showUpdateURLMessage, setShowUpdateURLMessage] = useState(false)
  const [showUpdateURLModal, setShowUpdateURLModal] = useState(false)
  const [showAddTrackingURLModal, setShowAddTrackingURLModal] = useState(false)
  const [menusToUpdate, setMenusToUpdate] = useState<Array<{
    menu: { id: string; title: string; handle: string; isDefault: boolean; items: any[] }
    updatedItems: any[]
  }>>([])
  const [updateMenuLoading, setUpdateMenuLoading] = useState(false)

  // 状态管理
  const [proRecomRegionalTipShow, setProRecomRegionalTipShow] = useState(false)
  const [proDiscountRegionalTipShow, setProDiscountRegionalTipShow] = useState(false)

  // Refs
  const PRUPositionRef = useRef<HTMLDivElement>(null)
  /** 用于判断「是否刚从 customizeBackdrop / customizeShowcase（App Window 主机）离开」，以便返回 appearance 等页时重新拉数 */
  const wasOnCustomizeAppWindowRouteRef = useRef<boolean | null>(null)

  // 高亮提示处理
  useEffect(() => {
    if (proDiscountRegionalTipShow || proRecomRegionalTipShow) {
      setTimeout(() => {
        PRUPositionRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        })
      }, 500)
    }
  }, [proDiscountRegionalTipShow, proRecomRegionalTipShow, loading])

  /**
   * 获取产品集合
   */
  const getProductCollection = useCallback(() => {
    fetchTrackingPageProductCollects()
  }, [])

  /**
   * 获取跟踪页面基础信息
   */
  const getData = useCallback(() => {
    fetchTrackingPageBaseData()
  }, [])

  // 初始化时处理路由状态；从 App Window 定制页关闭回到 tracking 子页时需重新请求（仅 mount 一次的 [] 无法覆盖）
  useEffect(() => {
    const locationState = location.state as { proRecom?: boolean; proDiscount?: boolean } | null

    if (locationState) {
      setProRecomRegionalTipShow(locationState.proRecom || false)
      setProDiscountRegionalTipShow(locationState.proDiscount || false)

      // 清空状态，防止二次渲染时携带了高亮参数
      navigate(location, { replace: true })
    }

    const isCustomizeAppWindowRoute = location.pathname === "/tracking-page/customizeBackdrop"
      || location.pathname === "/tracking-page/customizeShowcase"

    const prevWasCustomize = wasOnCustomizeAppWindowRouteRef.current

    wasOnCustomizeAppWindowRouteRef.current = isCustomizeAppWindowRoute

    // App Window 子路由由子页自行拉取（子 effect 先于父执行，避免看不到跟踪页接口）
    if (isCustomizeAppWindowRoute) {
      return
    }

    // 非定制子路由：首次进入，或从定制（App Window）返回 → 拉主区数据
    const shouldFetch = prevWasCustomize === null || prevWasCustomize === true

    if (!shouldFetch) {
      return
    }

    getData()
    getProductCollection()
  }, [location.pathname, location.state, navigate, getData, getProductCollection])

  /**
   * 处理保存操作
   */
  const handleSaveAction = useCallback(() => {
    const diffOptions = getDiffOptions()
    const data: Partial<ITrackingPageOptions> = {}

    Object.keys(diffOptions)
      .map(key => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        data[key] = cloneDeep(trackingPageStore[key])
        if (key === "customOrderStatus") {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          data[key] = data[key]?.ordered.children || []
        }
      })

    commonStore.setContextualSaveBar("loading", true)

    if (data.proRecommendAndUpsell?.productRecommendations.intent === ProductRecommendIntent.SpecificCollection
      && data.proRecommendAndUpsell?.productRecommendations.collect === 0) {
      trackingPageStore?.setRecommendationSelectCollectionError(true)
      commonStore.setContextualSaveBar("loading", false)
      return
    }

    if (data.translateTrackingDetailedInfo) {
      data.translateTrackingDetailedInfo = trackingPageStore.removeAnotherHandle() || []
    }

    if (
      (data.displayOption &&
        (isNaN(Number(data.displayOption?.themeContainerWidth)) || data.displayOption?.themeContainerWidth === 0)) ||
      (data.customOrderInput && data.customOrderInput.days === "0")
    ) {
      toast(t("common:SavedFailed"), 1000, true)
      commonStore.setContextualSaveBar("loading", false)
      return
    }

    if (data.post_purchase_edd?.non_business_day_periods?.length) {
      data.post_purchase_edd.non_business_day_periods = trackingPageStore.clearNonBusinessDayPeriods()
    }

    // 空文本检测
    if (data.translations) {
      const hasEmptyValue = Object.entries(data.translations)
        .some(([lang, trans]) => {
          return Object.entries(trans)
            .some(([key, value]) => {
              if (key === "features_text") {
                const featureText = value as FeaturesText

                const isEmptyCustomStatus = featureText.custom_status
                  .some(({ status, info }) => status?.trim() === "" || info?.trim() === "")

                return (
                  isEmptyCustomStatus
                  || featureText.additional_text_above?.trim() === ""
                  || featureText.additional_text_below?.trim() === ""
                  || featureText.custom_tracking_info?.trim() === ""
                )
              }

              return (value as string).trim() === ""
            })
        })

      if (hasEmptyValue) {
        toast(t("common:SavedFailed"), 2e3, true)
        commonStore.setContextualSaveBar("loading", false)
        return
      }
    }

    TPAPI.save(data)
      .then(({ data: response }) => {
        toast(
          response.msg || t("common:ServerError"),
          2e3,
          response.code !== APICode.OK,
        )
        const orignalUrl = TrackingPageUtils.getInitOptions().trackingPageUrl

        if (response.code === APICode.OK) {
          const { translations, translations_config } = response.data
          const recoverOptions = {
            translations,
            translations_config,
          }

          filterEmptyFeatureTextTrans(recoverOptions)

          // 修改初始值
          const newInitOptions = {
            ...cloneDeep(stores.trackingPageStore),
            ...recoverOptions,
          }

          TrackingPageUtils.setInitOptions(newInitOptions)
          trackingPageStore.setBaseOptions(recoverOptions)

          const { trackPageUrl } = stores.trackingPageStore

          // 更新 track_url
          if ("last_track_url" in userInfoSore.data) {
            userInfoSore.setDataSingle("last_track_url", `${userInfoSore.data.store_real_name}${trackPageUrl}`)
          }

          commonStore.setContextualSaveBar("show", false)

          eddModulesStore.setSaveBtnActive(false)

          window.onbeforeunload = null

          if ("showPPBranding" in userInfoSore.data) {
            userInfoSore.setDataSingle("showPPBranding", !trackingPageStore.brandingConfig?.isRemovedBranding)
          }

          // 如果修改了跟踪页面地址，需要进行检测
          if (data.trackingPageUrl){
            const { subpath, subpathPrefix } = orignalUrl || {}
            const ITEM_URL = `/${subpathPrefix || "apps"}/${subpath || "parcelpanel"}`
            const newTrackingURL = `/${data.trackingPageUrl.subpathPrefix || "apps"}/${data.trackingPageUrl.subpath || "parcelpanel"}`

            checkAddTrackingPage(ITEM_URL, newTrackingURL).then(({
              isCompleted, itemsCount, menusToUpdate,
            }) => {
              console.log(isCompleted)
              console.log("更新菜单项：", itemsCount)
              // 存储需要更新的菜单数据
              setMenusToUpdate(menusToUpdate)

              // 新手引导已完成
              if (isCompleted) {
                // 匹配到 0 个 Item → 仅文本提醒
                if (itemsCount === 0){
                  setShowUpdateURLMessage(true)
                } else {
                  // 匹配到 1 个以上的 Items
                  setShowUpdateURLModal(true)
                }
              } else {
                // 匹配到 0 个 Item → 添加 Item：同 Quick setup guide 中的操作
                if (itemsCount === 0){
                  setShowAddTrackingURLModal(true)
                } else {
                  // 匹配到 1 个以上的 Items
                  setShowUpdateURLModal(true)
                }
              }
            })
          }
        }
      })
      .catch((e) => {
        toast(
          t("common:SavedFailed"),
          2e3,
          true,
        )
      })
      .finally(() => {
        commonStore.setContextualSaveBar("loading", false)
      })
  }, [trackingPageStore, commonStore, eddModulesStore, t])

  /**
   * 更新菜单
   */
  const handleUpdateURL = async () => {
    if (menusToUpdate.length === 0) {
      toast(t("common:SavedFailed"), 1000, true)
      return
    }

    setUpdateMenuLoading(true)
    try {
      // 使用 Promise.all 批量更新菜单
      const updatePromises = menusToUpdate.map(({ menu, updatedItems }) =>
        ShopifyGraphql.setMenu(menu.id, menu.title, updatedItems),
      )

      await Promise.all(updatePromises)

      toast(t("common:UpdatedSuccessfully"), 1000)
      setShowUpdateURLModal(false)
      setMenusToUpdate([])
    } catch (error) {
      console.error("批量更新菜单失败:", error)
      toast(t("common:SavedFailed"), 1000, true)
    } finally {
      setUpdateMenuLoading(false)
    }
  }
  /**
   * 处理放弃操作
   */
  const handleDiscardAction = useCallback(() => {
    commonStore.setContextualSaveBar("loading", false)
    commonStore.setContextualSaveBar("show", false)

    // 还原回初始值
    TrackingPageUtils.destroyListener() // 先销毁监听器
    trackingPageStore.setBaseOptions(TrackingPageUtils.getInitOptions()) // 然后还原

    // 初始化edd module config
    if (!eddModulesStore.saveBtnActive) {
      initModulesStore()
    }
    TrackingPageUtils.registerListener() // 最后再注册监听器

    stores.userInfoSore.setDataSingle("showPPBranding", !trackingPageStore.brandingConfig?.isRemovedBranding)
  }, [trackingPageStore, commonStore, eddModulesStore])

  // 撤销 / 重做（保存快捷键由 SaveModule 统一处理）
  useEditorShortcuts({
    enabled: !loading && !isThemeLayoutCustomizeFullWidthRoute,
    onUndo: () => {
      if (!commonStore.contextualSaveBar.loading) {
        trackingPageHistory.undo()
      }
    },
    onRedo: () => {
      if (!commonStore.contextualSaveBar.loading) {
        trackingPageHistory.redo()
      }
    },
  })

  // 渲染组件
  return (
    <div>
      <Container
        id="Page-TrackingPage"
        className={classNames("mt-0", isThemeLayoutCustomizeFullWidthRoute && "w-full max-w-none !px-0")}
        fullWidth={isThemeLayoutCustomizeFullWidthRoute}
        maxWidth="1036px"
      >
        {!isThemeLayoutCustomizeFullWidthRoute && (
          <SaveModule
            subRouteBlock
            onSave={handleSaveAction}
            onDiscard={handleDiscardAction}
            show={commonStore.contextualSaveBar.show}
            loading={commonStore.contextualSaveBar.loading}
            disabled={commonStore.contextualSaveBar.disabled}
          />
        )}

        {/* 这里是新开的TrackingPage*/}
        <TabPage loading={loading} />

        {/* PRU位置参考 */}
        <div ref={PRUPositionRef} />

        {/* Tracking url modal */}
        <UpdateURLMessage
          open={showUpdateURLMessage}
          preventCloseOnClickOutside
          onClose={() => setShowUpdateURLMessage(false)}
        />
        <UpdateURLModal
          preventCloseOnClickOutside
          loading={updateMenuLoading}
          open={showUpdateURLModal}
          onUpdate={handleUpdateURL}
          onClose={() => setShowUpdateURLModal(false)}
        />
        <AddTrackingPageModal
          show={showAddTrackingURLModal}
          onClose={() => setShowAddTrackingURLModal(false)}
          trackURL={getShortTrackLink(userInfoSore.data?.last_track_url || "")}
        />
      </Container>
    </div>
  )
})

export default TrackingPage
